import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Navbar from "./components/Navbar";
import { DarkModeProvider, DarkModeContext } from "./context/DarkModeContext";
import { lazy, Suspense, useContext, useEffect, useMemo } from "react";
import { PlanningProvider } from "./context/PlanningContext";
import { UiSettingsProvider } from "./context/UiSettingsContext";
import { GridDensityProvider } from "./context/GridDensityContext";
import { UserProvider } from "./context/UserContext";
import { LicenseProvider } from "./context/LicenseContext";
import { TenantProvider } from "./context/TenantContext";
import SudoBanner from "./admin/SudoBanner";
import QuickActions from "./components/QuickActions";
import GlobalSearch from "./components/GlobalSearch";
import OnboardingProvider from "./components/OnboardingProvider";
import CapitalLayout from "./components/capital/CapitalLayout";
import ModuleLayout from "./components/modules/ModuleLayout";
import ModulesLayout from "./components/modules/ModulesLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAUpdatePrompt from "./components/PWAUpdatePrompt";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import PoweredByFooter from "./components/PoweredByFooter";
import createMuiTheme from "./theme/muiTheme";

// ---------------------------------------------------------------------------
// Lazy-loaded page components — each is code-split into its own chunk.
// ---------------------------------------------------------------------------
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BudgetOverview = lazy(() => import("./pages/BudgetOverview"));
const DepartmentDetail = lazy(() => import("./pages/DepartmentDetail"));
const DepartmentOverview = lazy(() => import("./pages/DepartmentOverview"));
const FieldMapping = lazy(() => import("./components/FieldMapping"));
const SupersetDashboard = lazy(() => import("./pages/SupersetDashboard"));
const FeeProjection = lazy(() => import("./pages/FeeProjection"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const CampusSummary = lazy(() => import("./pages/CampusSummary"));
const CoursePlanningPage = lazy(() => import("./pages/CoursePlanningPage"));
const PersonnelDetail = lazy(() => import("./pages/PersonnelDetail"));
const PositionBudgeting = lazy(() => import("./pages/PositionBudgeting"));
const BudgetModel = lazy(() => import("./pages/BudgetModel"));
const BudgetBookReport = lazy(() => import("./pages/BudgetBookReport"));
const DeficitCostCenters = lazy(() => import("./pages/DeficitCostCenters"));
const InvalidFundingStrings = lazy(() => import("./pages/InvalidFundingStrings"));
const BudgetTransfers = lazy(() => import("./pages/BudgetTransfers"));
const AllocationEditor = lazy(() => import("./pages/AllocationEditor"));
const AllocationApprovals = lazy(() => import("./pages/AllocationApprovals"));
const MyAllocationSubmissions = lazy(() => import("./pages/MyAllocationSubmissions"));
const DepartmentPlanningPage = lazy(() => import("./pages/DepartmentPlanningPage"));
const PublicFormPage = lazy(() => import("./pages/PublicFormPage"));
const WorkflowStatusPage = lazy(() => import("./pages/WorkflowPage"));
const MySubmissionsPage = lazy(() => import("./pages/MySubmissionsPage"));
const AccessibleSubmissionsPage = lazy(() => import("./pages/AccessibleSubmissionsPage"));
const SubmissionFormPage = lazy(() => import("./pages/SubmissionFormPage"));
const CustomValidationDoc = lazy(() => import("./pages/CustomValidationDoc"));
const DataImportDoc = lazy(() => import("./pages/DataImportDoc"));
const FormBuilderDoc = lazy(() => import("./pages/FormBuilderDoc"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const HelpVideos = lazy(() => import("./pages/HelpVideos"));
const HelpFaq = lazy(() => import("./pages/HelpFaq"));
const HelpWalkthroughs = lazy(() => import("./pages/HelpWalkthroughs"));
const HelpContact = lazy(() => import("./pages/HelpContact"));
const TeamsTabConfig = lazy(() => import("./pages/TeamsTabConfig"));
const TeamsTabContent = lazy(() => import("./pages/TeamsTabContent"));

// Tuition & Budget admin pages
const CostOfAttendanceEditor = lazy(() => import("./admin/CostOfAttendanceEditor"));
const BudgetVsActualReport = lazy(() => import("./admin/BudgetVsActualReport"));
const DiscountPoliciesEditor = lazy(() => import("./admin/DiscountPoliciesEditor"));
const FeeComponentsEditor = lazy(() => import("./admin/FeeComponentsEditor"));
const AdmissionFunnelChart = lazy(() => import("./admin/AdmissionFunnelChart"));
const YieldAnalysisDashboard = lazy(() => import("./admin/YieldAnalysisDashboard"));
const ProgramMarginDashboard = lazy(() => import("./admin/ProgramMarginDashboard"));
const ScenarioComparisonDashboard = lazy(() => import("./admin/ScenarioComparisonDashboard"));
const RevenueRecognitionDashboard = lazy(() => import("./admin/RevenueRecognitionDashboard"));

// Research pages
const Research = lazy(() => import("./pages/Research"));
const ResearchDetail = lazy(() => import("./pages/ResearchDetail"));
const ResearchPortfolio = lazy(() => import("./pages/ResearchPortfolio"));
const ResearchSettings = lazy(() => import("./pages/ResearchSettings"));
const ResearchSubawards = lazy(() => import("./pages/ResearchSubawards"));
const ResearchSubawardDetail = lazy(() => import("./pages/ResearchSubawardDetail"));
const ResearchExplorer = lazy(() => import("./pages/ResearchExplorer"));
const ResearchProposals = lazy(() => import("./pages/ResearchProposals"));
const ResearchProposalDetail = lazy(() => import("./pages/ResearchProposalDetail"));
const ResearchBudgetBuilder = lazy(() => import("./pages/ResearchBudgetBuilder"));
const ResearchAnalytics = lazy(() => import("./pages/ResearchAnalytics"));
const ResearchFinancialHealth = lazy(() => import("./pages/ResearchFinancialHealth"));
const ResearchPortfolioPerformance = lazy(() => import("./pages/ResearchPortfolioPerformance"));
const ResearchComplianceRisk = lazy(() => import("./pages/ResearchComplianceRisk"));
const ResearchPersonnelEffort = lazy(() => import("./pages/ResearchPersonnelEffort"));
const ResearchCloseoutReadiness = lazy(() => import("./pages/ResearchCloseoutReadiness"));
const ResearchWorkload = lazy(() => import("./pages/ResearchWorkload"));
const ResearchLimitedSubmissions = lazy(() => import("./pages/ResearchLimitedSubmissions"));
const ResearchJITRequests = lazy(() => import("./pages/ResearchJITRequests"));
const ResearchJITRequestDetail = lazy(() => import("./pages/ResearchJITRequestDetail"));
const ResearchPASRequests = lazy(() => import("./pages/ResearchPASRequests"));
const ResearchPASRequestDetail = lazy(() => import("./pages/ResearchPASRequestDetail"));
const ResearchLimitedSubmissionDetail = lazy(() => import("./pages/ResearchLimitedSubmissionDetail"));
const ResearchDocumentRules = lazy(() => import("./pages/ResearchDocumentRules"));
const ResearchDocumentBuilder = lazy(() => import("./pages/ResearchDocumentBuilder"));
const ResearchApprovalWorkflows = lazy(() => import("./pages/ResearchApprovalWorkflows"));
const ResearchTrainingCompliance = lazy(() => import("./pages/ResearchTrainingCompliance"));
const ResearchFAWaiverRequests = lazy(() => import("./pages/ResearchFAWaiverRequests"));
const ResearchSubmissionSettings = lazy(() => import("./pages/ResearchSubmissionSettings"));

// Capital pages
const CapitalProjects = lazy(() => import("./pages/CapitalProjects"));
const CapitalProjectDetail = lazy(() => import("./pages/CapitalProjectDetail"));
const CapitalPlanning = lazy(() => import("./pages/CapitalPlanning"));
const CapitalPortfolioDashboard = lazy(() => import("./pages/CapitalPortfolioDashboard"));
const BudgetActualDashboard = lazy(() => import("./components/capital/BudgetActualDashboard"));

// Suspense fallback shown while lazy chunks load
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

// Helper component to redirect to admin routes
const RedirectToAdmin = ({ path }) => {
  useEffect(() => {
    window.location.href = `/admin${path}`;
  }, [path]);
  return null;
};

const Layout = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isTeamsRoute = location.pathname.startsWith("/teams");
  const isAuthRoute = ["/login", "/forgot-password", "/reset-password"].some(
    (p) => location.pathname.startsWith(p),
  );

  // Teams routes render without the normal layout (no navbar, etc.)
  if (isTeamsRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Teams Tab Configuration Page */}
          <Route path="/teams/config" element={<TeamsTabConfig />} />

          {/* Teams Tab Content Routes */}
          <Route
            path="/teams/tab/dashboard"
            element={<TeamsTabContent contentType="dashboard" />}
          />
          <Route
            path="/teams/tab/superset"
            element={<TeamsTabContent contentType="superset" />}
          />
          <Route
            path="/teams/tab/department/:departmentId/:departmentName?"
            element={<TeamsTabContent contentType="department" />}
          />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <SudoBanner />
      {!isAdmin && !isAuthRoute && <Navbar />}
      <main id="main-content" tabIndex="-1">
        <Suspense fallback={<PageLoader />}>
        <Routes key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Admin-only routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute roles={["admin", "superadmin"]}>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ModulesLayout defaultCollapsed>
                  <Dashboard />
                </ModulesLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <BudgetOverview />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/:name"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <BudgetOverview />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Navigate to="/" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mapping"
            element={
              <ProtectedRoute>
                <FieldMapping />
              </ProtectedRoute>
            }
          />
          <Route
            path="/department/:id/:name?"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <DepartmentOverview />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/department/details/:id"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <DepartmentDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campus-summary"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <CampusSummary />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfers"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <BudgetTransfers />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/allocations"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <AllocationEditor />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <AllocationApprovals />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-submissions"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <MyAllocationSubmissions />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning/:deptId/:fiscalYear/:scenarioId?"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <DepartmentPlanningPage />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <DepartmentPlanningPage />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/settings"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchSettings />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/workload"
            element={
              <ProtectedRoute roles={["admin", "superadmin", "grant_admin", "grant_accountant", "research_director", "finance_director"]}>
                <ModuleLayout moduleKey="research">
                  <ResearchWorkload />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/portfolio"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchPortfolio />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/explorer"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchExplorer />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/*"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <Research />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/:id/settings"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchDetail initialTab="settings" />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchProposals />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals/:id"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchProposalDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals/:proposalId/budget"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchBudgetBuilder />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals/:proposalId/document-builder"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchDocumentBuilder />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/document-builder"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchDocumentBuilder />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/limited-submissions"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchLimitedSubmissions />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/limited-submissions/:competitionId"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchLimitedSubmissionDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/jit-requests"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchJITRequests />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals/:proposalId/jit/:jitId"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchJITRequestDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/pas-requests"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchPASRequests />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/proposals/:proposalId/pas/:pasId"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchPASRequestDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/document-rules"
            element={
              <ProtectedRoute roles={["admin", "superadmin", "grant_admin", "research_director"]}>
                <ModuleLayout moduleKey="research">
                  <ResearchDocumentRules />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/approval-workflows"
            element={
              <ProtectedRoute roles={["admin", "superadmin", "research_director"]}>
                <ModuleLayout moduleKey="research">
                  <ResearchApprovalWorkflows />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/training-compliance"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchTrainingCompliance />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/fa-waiver-requests"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchFAWaiverRequests />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/submission-settings"
            element={
              <ProtectedRoute roles={["admin", "superadmin", "research_director"]}>
                <ModuleLayout moduleKey="research">
                  <ResearchSubmissionSettings />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/analytics"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchAnalytics />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/financial-health"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchFinancialHealth />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/portfolio-performance"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchPortfolioPerformance />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/compliance-risk"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchComplianceRisk />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/personnel-effort"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchPersonnelEffort />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/closeout-readiness"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchCloseoutReadiness />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/subawards"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchSubawards />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/subawards/:id"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchSubawardDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research/:id"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="research">
                  <ResearchDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          {/* Redirect to admin page for research assignment management */}
          <Route
            path="/research-assignments"
            element={<RedirectToAdmin path="/research-assignments" />}
          />
          {/* Capital Planning Routes */}
          <Route
            path="/capital"
            element={
              <ProtectedRoute>
                <CapitalLayout>
                  <CapitalProjects />
                </CapitalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/capital/planning"
            element={
              <ProtectedRoute>
                <CapitalLayout>
                  <CapitalPlanning />
                </CapitalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/capital/portfolio"
            element={
              <ProtectedRoute>
                <CapitalLayout>
                  <CapitalPortfolioDashboard />
                </CapitalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/capital/budget-actual"
            element={
              <ProtectedRoute>
                <CapitalLayout>
                  <BudgetActualDashboard />
                </CapitalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/capital/:id"
            element={
              <ProtectedRoute>
                <CapitalLayout>
                  <CapitalProjectDetail />
                </CapitalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/form/:table_name"
            element={
              <ProtectedRoute>
                <PublicFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflow/:submission_id"
            element={
              <ProtectedRoute>
                <WorkflowStatusPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-submissions"
            element={
              <ProtectedRoute>
                <MySubmissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accessible-submissions"
            element={
              <ProtectedRoute>
                <AccessibleSubmissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submission/:submissionId"
            element={
              <ProtectedRoute>
                <SubmissionFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deficits"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <DeficitCostCenters />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invalid-funding"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <InvalidFundingStrings />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superset/*"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <SupersetDashboard />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/projection"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <FeeProjection />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/cost-of-attendance"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <CostOfAttendanceEditor />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/discounts"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <DiscountPoliciesEditor />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/components"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <FeeComponentsEditor />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enrollment/funnel"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <AdmissionFunnelChart />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enrollment/yield"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="tuition">
                  <YieldAnalysisDashboard />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/vs-actual"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <BudgetVsActualReport />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/book"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <BudgetBookReport />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/scenarios"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="budget">
                  <ScenarioComparisonDashboard />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/margins"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <ProgramMarginDashboard />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget/revenue-recognition"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="reporting">
                  <RevenueRecognitionDashboard />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/personnel/:id"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <PersonnelDetail />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/custom-validation"
            element={
              <ProtectedRoute>
                <CustomValidationDoc />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/data-import"
            element={
              <ProtectedRoute>
                <DataImportDoc />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/form-builder"
            element={
              <ProtectedRoute>
                <FormBuilderDoc />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/videos"
            element={
              <ProtectedRoute>
                <HelpVideos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/faq"
            element={
              <ProtectedRoute>
                <HelpFaq />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/contact"
            element={
              <ProtectedRoute>
                <HelpContact />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/walkthroughs"
            element={
              <ProtectedRoute>
                <HelpWalkthroughs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="courses">
                  <CoursePlanningPage />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/positions/budgeting"
            element={
              <ProtectedRoute>
                <ModuleLayout moduleKey="positions">
                  <PositionBudgeting />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget-model"
            element={
              <ProtectedRoute roles={["admin", "superadmin", "budget_owner"]}>
                <ModuleLayout moduleKey="budget">
                  <BudgetModel />
                </ModuleLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </main>
    </>
  );
};

const AppContent = () => {
  const { darkMode } = useContext(DarkModeContext);
  const muiTheme = useMemo(
    () => createMuiTheme(darkMode ? "dark" : "light"),
    [darkMode],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      <div
        className={`min-h-screen transition-colors duration-300 ${darkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" : "bg-gradient-to-br from-slate-50 via-white to-indigo-50 text-gray-900"}`}
      >
        <OnboardingProvider>
          <Layout />
          <QuickActions />
          <GlobalSearch />
        </OnboardingProvider>
        <ToastContainer theme={darkMode ? "dark" : "light"} />
        <PWAUpdatePrompt />
        <PWAInstallPrompt />
        <OfflineIndicator />
        <PoweredByFooter />
      </div>
    </ThemeProvider>
  );
};

// Configure React Query with optimal defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus (can be annoying during development)
      refetchOnWindowFocus: false,
      // Refetch on network reconnect
      refetchOnReconnect: true,
      // Retry failed queries once
      retry: 1,
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Don't retry mutations by default
      retry: 0,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router>
          <DarkModeProvider>
            <PlanningProvider>
              <UiSettingsProvider>
                <GridDensityProvider>
                  <TenantProvider>
                    <UserProvider>
                      <LicenseProvider>
                        <AppContent />
                      </LicenseProvider>
                    </UserProvider>
                  </TenantProvider>
                </GridDensityProvider>
              </UiSettingsProvider>
            </PlanningProvider>
          </DarkModeProvider>
        </Router>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
